import { useQuery } from "@tanstack/react-query";

import type { BillingEstimate } from "features/subscriptions/types";

import { CUSTOMER_QUERY_KEYS } from "common/constants";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { get } from "common/helpers/HTTP";

async function workspaceEstimateRenewalRequest(
  workspaceId: string,
  subscriptionId: string,
): Promise<BillingEstimate> {
  const { estimate } = await get(
    `workspaces/${workspaceId}/subscriptions/${subscriptionId}/estimates/renew`,
  );
  return estimate;
}

async function customerEstimateRenewalRequest(): Promise<BillingEstimate> {
  const { estimate } = await get(`subscriptions/estimates/renew`);
  return estimate;
}

//if subscriptionId is not provided, this is a customer estimate
export default function useEstimateRenewal(subscriptionId?: string) {
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const isCustomer = !subscriptionId;

  const {
    data: estimateRenewal,
    isFetching: isLoadingEstimateRenewal,
    error: estimateRenewalError,
  } = useQuery({
    queryKey: isCustomer
      ? CUSTOMER_QUERY_KEYS.subscriptionKeys.renewalEstimate()
      : getQueryKeys(workspaceId).subscriptionKeys.renewalEstimate(
          subscriptionId,
        ),
    queryFn: () => {
      if (isCustomer) {
        return customerEstimateRenewalRequest();
      }

      if (!subscriptionId) {
        throw new Error("subscriptionId is required for workspace estimates");
      }

      return workspaceEstimateRenewalRequest(workspaceId, subscriptionId);
    },
    enabled: isCustomer || !!subscriptionId,
    refetchOnMount: false,
  });

  return {
    estimateRenewal,
    isLoadingEstimateRenewal,
    estimateRenewalError,
  };
}
